import React from "react";

interface Props {
  label?: string;
  fullScreen?: boolean;
  size?: "small" | "medium" | "large";
  variant?: "default" | "primary" | "gradient";
}

const Loading: React.FC<Props> = ({
  label = "Loading...",
  fullScreen = false,
  size = "medium",
  variant = "primary",
}) => {
  const sizeClasses = {
    small: "w-8 h-8 border-3",
    medium: "w-12 h-12 border-4",
    large: "w-16 h-16 border-5",
  };

  const variantClasses = {
    default: "border-gray-400 border-t-gray-700",
    primary: "border-red-100 border-t-red-600",
    gradient:
      "border-gray-200 border-t-gradient-to-r from-red-500 to-purple-600",
  };

  const textSizes = {
    small: "text-sm",
    medium: "text-base",
    large: "text-lg",
  };

  const loadingContent = (
    <div className="flex flex-col items-center justify-center">
      {/* Modern Spinner */}
      <div className="relative">
        {/* Outer ring */}
        <div
          className={`${sizeClasses[size]} ${variantClasses[variant]} rounded-full animate-spin`}
        ></div>

        {/* Inner ring for gradient variant */}
        {variant === "gradient" && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className={`${
                size === "small"
                  ? "w-4 h-4"
                  : size === "medium"
                    ? "w-6 h-6"
                    : "w-8 h-8"
              } bg-gradient-to-r from-red-400 to-purple-400 rounded-full animate-pulse`}
            ></div>
          </div>
        )}

        {/* Dots for other variants */}
        {variant !== "gradient" && (
          <>
            <div
              className={`absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1 ${
                size === "small"
                  ? "w-1 h-1"
                  : size === "medium"
                    ? "w-1.5 h-1.5"
                    : "w-2 h-2"
              } ${
                variant === "primary" ? "bg-red-500" : "bg-gray-700"
              } rounded-full`}
            ></div>
            <div
              className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1 ${
                size === "small"
                  ? "w-1 h-1"
                  : size === "medium"
                    ? "w-1.5 h-1.5"
                    : "w-2 h-2"
              } ${
                variant === "primary" ? "bg-red-500" : "bg-gray-700"
              } rounded-full`}
            ></div>
          </>
        )}
      </div>

      {/* Label with typing animation */}
      <div className="mt-4 text-center">
        <span
          className={`${textSizes[size]} font-medium text-gray-700 tracking-wide`}
        >
          {label}
        </span>
        {/* Animated dots */}
        <div className="inline-flex items-center ml-1">
          <span
            className="w-1 h-1 bg-gray-400 rounded-full animate-bounce"
            style={{ animationDelay: "0ms" }}
          ></span>
          <span
            className="w-1 h-1 bg-gray-400 rounded-full animate-bounce ml-0.5"
            style={{ animationDelay: "150ms" }}
          ></span>
          <span
            className="w-1 h-1 bg-gray-400 rounded-full animate-bounce ml-0.5"
            style={{ animationDelay: "300ms" }}
          ></span>
        </div>
      </div>

      {/* Progress bar (optional) */}
      {size === "large" && (
        <div className="w-32 h-1 bg-gray-200 rounded-full overflow-hidden mt-4">
          <div className="h-full bg-gradient-to-r from-red-500 to-red-400 rounded-full animate-progress"></div>
        </div>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-white via-gray-50 to-red-50/30 backdrop-blur-sm">
        <div className="bg-white/90 p-8 rounded-2xl shadow-2xl border border-gray-100">
          {loadingContent}
        </div>
      </div>
    );
  }

  return <div className="p-8">{loadingContent}</div>;
};

export default Loading;
